import { trackCalculatorComplete } from "@/components/analytics/conversions";
import { CalculatorArticle } from "@/components/calculators/calculator-article";
import { calculatorArticleHead } from "@/components/calculators/calculator-article-head";
import { ffmiGuide } from "@/components/calculators/guides/ffmi-guide";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { clearFfmiHandoff, readFfmiHandoff } from "@/utils/ffmi-handoff";
import { seo } from "@/utils/seo";
import { createFileRoute } from "@tanstack/react-router";
import { Dumbbell, Info, Target } from "lucide-react";
import { useEffect, useState } from "react";

export const Route = createFileRoute("/_app/essay/ffmi-calculator")({
  component: FFMICalculator,
  head: () => ({
    meta: seo({
      title: "FFMI Hesaplayıcı ve Yağsız Kütle Rehberi | AtalayTheCoach",
      description:
        "Yağsız kütle indeksini ve normalize FFMI değerini hesapla; yağ oranı ölçümünün etkisini ve sonuçların sınırlarını öğren.",
      keywords:
        "FFMI hesaplama, fat free mass index, yağsız kütle, normalize FFMI, vücut kompozisyonu, Alican Atalay",
      canonical: "https://atalaythecoach.com/essay/ffmi-calculator",
    }),
    ...calculatorArticleHead({
      canonical: "https://atalaythecoach.com/essay/ffmi-calculator",
      headline:
        "FFMI Hesaplayıcı: Yağsız Kütle İndeksini Doğru Yorumlama Rehberi",
      description:
        "Ham ve normalize FFMI hesabı, yağ oranı ölçümünün etkisi ve sonuçların güvenli yorumlanması.",
    }),
  }),
});

// FFMI Hesaplama Fonksiyonu
function calculateFFMI(
  weight: number,
  height: number,
  bodyFatPercentage: number,
): number {
  const heightInMeters = height / 100;
  const fatMass = (weight * bodyFatPercentage) / 100;
  const fatFreeMass = weight - fatMass;
  const ffmi = fatFreeMass / (heightInMeters * heightInMeters);
  return ffmi;
}

function calculateNormalizedFFMI(ffmi: number, height: number): number {
  const heightInMeters = height / 100;
  return ffmi + 6.1 * (1.8 - heightInMeters);
}

const getFFMICategory = (normalizedFFMI: number, gender: string) => {
  const categories = {
    male: [
      {
        min: 0,
        max: 16,
        level: "Düşük",
        color: "bg-destructive/10 text-destructive border-destructive/20",
        desc: "Kas kütlesi yetersiz",
      },
      {
        min: 16,
        max: 17,
        level: "Ortalama Altı",
        color: "bg-accent/20 text-accent-foreground border-accent/30",
        desc: "Gelişim potansiyeli var",
      },
      {
        min: 17,
        max: 18,
        level: "Ortalama",
        color: "bg-muted/30 text-muted-foreground border-muted",
        desc: "Normal kas kütlesi",
      },
      {
        min: 18,
        max: 20,
        level: "Ortalama Üzeri",
        color: "bg-primary/10 text-primary border-primary/20",
        desc: "İyi kas gelişimi",
      },
      {
        min: 20,
        max: 22,
        level: "Mükemmel",
        color: "bg-primary/20 text-primary border-primary/30",
        desc: "Çok iyi kas kütlesi",
      },
      {
        min: 22,
        max: 23,
        level: "Elit",
        color: "bg-accent/30 text-accent-foreground border-accent",
        desc: "Elit seviye kas kütlesi",
      },
      {
        min: 23,
        max: 25,
        level: "Süper Elit",
        color: "bg-primary/30 text-primary border-primary/40",
        desc: "Doğal limit yakın",
      },
      {
        min: 25,
        max: 100,
        level: "Doğal Limit Üzeri",
        color: "bg-destructive/10 text-destructive border-destructive/20",
        desc: "Şüpheli seviye",
      },
    ],
    female: [
      {
        min: 0,
        max: 14,
        level: "Düşük",
        color: "bg-destructive/10 text-destructive border-destructive/20",
        desc: "Kas kütlesi yetersiz",
      },
      {
        min: 14,
        max: 15,
        level: "Ortalama Altı",
        color: "bg-accent/20 text-accent-foreground border-accent/30",
        desc: "Gelişim potansiyeli var",
      },
      {
        min: 15,
        max: 16,
        level: "Ortalama",
        color: "bg-muted/30 text-muted-foreground border-muted",
        desc: "Normal kas kütlesi",
      },
      {
        min: 16,
        max: 17,
        level: "Ortalama Üzeri",
        color: "bg-primary/10 text-primary border-primary/20",
        desc: "İyi kas gelişimi",
      },
      {
        min: 17,
        max: 18,
        level: "Mükemmel",
        color: "bg-primary/20 text-primary border-primary/30",
        desc: "Çok iyi kas kütlesi",
      },
      {
        min: 18,
        max: 19,
        level: "Elit",
        color: "bg-accent/30 text-accent-foreground border-accent",
        desc: "Elit seviye kas kütlesi",
      },
      {
        min: 19,
        max: 20,
        level: "Süper Elit",
        color: "bg-primary/30 text-primary border-primary/40",
        desc: "Doğal limit yakın",
      },
      {
        min: 20,
        max: 100,
        level: "Doğal Limit Üzeri",
        color: "bg-destructive/10 text-destructive border-destructive/20",
        desc: "Şüpheli seviye",
      },
    ],
  };

  const genderCategories = categories[gender as keyof typeof categories];

  for (const category of genderCategories) {
    if (normalizedFFMI >= category.min && normalizedFFMI < category.max) {
      return category;
    }
  }

  return genderCategories[genderCategories.length - 1];
};

// Doğal Potansiyel Hesaplama
function calculateNaturalPotential(height: number, gender: string) {
  const maxFFMI = gender === "male" ? 25 : 20;
  const heightInMeters = height / 100;
  const maxLeanMass = maxFFMI * (heightInMeters * heightInMeters);

  return {
    maxFFMI,
    maxLeanMass: maxLeanMass,
    maxWeightAt10BF: maxLeanMass / 0.9, // %10 vücut yağında maksimum kilo
    maxWeightAt15BF: maxLeanMass / 0.85, // %15 vücut yağında maksimum kilo
  };
}

function FFMICalculator() {
  const [gender, setGender] = useState<string>("");
  const [height, setHeight] = useState<string>("");
  const [weight, setWeight] = useState<string>("");
  const [bodyFat, setBodyFat] = useState<string>("");
  const [results, setResults] = useState<{
    ffmi: number;
    normalizedFFMI: number;
    category: any;
    fatFreeMass: number;
    fatMass: number;
    naturalPotential: any;
  } | null>(null);

  useEffect(() => {
    const handoff = readFfmiHandoff();
    if (!handoff) return;

    setGender(handoff.gender);
    setHeight(String(handoff.height));
    setBodyFat(handoff.bodyFat.toFixed(1));
    if (handoff.weight !== undefined) {
      setWeight(String(handoff.weight));
    }
    window.setTimeout(clearFfmiHandoff, 500);
  }, []);

  const calculateResults = () => {
    const heightNum = Number.parseFloat(height);
    const weightNum = Number.parseFloat(weight);
    const bodyFatNum = Number.parseFloat(bodyFat);

    if (!heightNum || !weightNum || !bodyFatNum || !gender) return;

    if (bodyFatNum < 3 || bodyFatNum > 50) {
      alert("Vücut yağ oranı 3-50% arasında olmalıdır");
      return;
    }

    const ffmi = calculateFFMI(weightNum, heightNum, bodyFatNum);
    const normalizedFFMI = calculateNormalizedFFMI(ffmi, heightNum);
    const category = getFFMICategory(ffmi, gender);

    const fatMass = (weightNum * bodyFatNum) / 100;
    const fatFreeMass = weightNum - fatMass;

    const naturalPotential = calculateNaturalPotential(heightNum, gender);

    setResults({
      ffmi,
      normalizedFFMI,
      category,
      fatFreeMass,
      fatMass,
      naturalPotential,
    });
    trackCalculatorComplete("ffmi");
  };

  return (
    <div className="min-h-screen bg-background pt-32 pb-16">
      <div className="container mx-auto max-w-5xl px-4">
        {/* Header */}
        <div className="mb-12 text-center">
          <div className="mb-6 flex justify-center">
            <div className="rounded-full bg-primary/10 p-4 dark:bg-primary/20">
              <Dumbbell className="h-12 w-12 text-primary" />
            </div>
          </div>
          <h1 className="mb-6 font-bold text-4xl text-foreground leading-tight md:text-5xl lg:text-6xl">
            FFMI Hesaplayıcı
          </h1>
          <p className="mx-auto max-w-3xl text-lg text-muted-foreground leading-relaxed md:text-xl">
            Fat-Free Mass Index (FFMI) ile kas kütlenizi analiz edin. Doğal
            potansiyelinizi keşfedin ve hedeflerinizi belirleyin.
          </p>
        </div>

        <div className="grid gap-8 lg:grid-cols-3">
          {/* Hesaplayıcı Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Dumbbell className="h-5 w-5 text-primary" />
                  FFMI Hesapla
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-3">
                    <Label>Cinsiyet</Label>
                    <RadioGroup value={gender} onValueChange={setGender}>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="male" id="male-ffmi" />
                        <Label htmlFor="male-ffmi">Erkek</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="female" id="female-ffmi" />
                        <Label htmlFor="female-ffmi">Kadın</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="height">Boy (cm)</Label>
                    <Input
                      id="height"
                      type="number"
                      placeholder="175"
                      value={height}
                      onChange={(e) => setHeight(e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="weight">Kilo (kg)</Label>
                    <Input
                      id="weight"
                      type="number"
                      placeholder="75"
                      value={weight}
                      onChange={(e) => setWeight(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="bodyFat">Vücut Yağ Oranı (%)</Label>
                    <Input
                      id="bodyFat"
                      type="number"
                      placeholder="12"
                      step="0.1"
                      value={bodyFat}
                      onChange={(e) => setBodyFat(e.target.value)}
                    />
                  </div>
                </div>

                <div className="rounded-lg border border-primary/20 bg-primary/10 p-4 dark:border-primary/30 dark:bg-primary/20">
                  <h4 className="mb-2 flex items-center gap-2 font-semibold text-foreground">
                    <Info className="h-4 w-4 text-primary" />
                    Vücut Yağ Oranını Nasıl Öğrenebilirim?
                  </h4>
                  <p className="mb-3 text-muted-foreground text-sm">
                    Doğru FFMI hesaplama için vücut yağ oranınızı bilmeniz
                    gerekiyor:
                  </p>
                  <ul className="space-y-1 text-muted-foreground text-sm">
                    <li>
                      •{" "}
                      <strong className="text-foreground">
                        Body Fat Hesaplayıcımızı
                      </strong>{" "}
                      kullanın (Kaliper, Navy method)
                    </li>
                    <li>
                      •{" "}
                      <strong className="text-foreground">DEXA taraması</strong>{" "}
                      yaptırın (en hassas)
                    </li>
                    <li>
                      • <strong className="text-foreground">BodPod</strong> veya
                      su altı tartımı
                    </li>
                    <li>
                      •{" "}
                      <strong className="text-foreground">
                        Bioelectrical impedance
                      </strong>{" "}
                      terazisi (yaklaşık)
                    </li>
                  </ul>
                </div>

                <Button
                  onClick={calculateResults}
                  className="w-full"
                  disabled={!height || !weight || !bodyFat || !gender}
                >
                  FFMI Hesapla
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Sonuçlar */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-primary" />
                  Sonuçlarınız
                </CardTitle>
              </CardHeader>
              <CardContent>
                {results ? (
                  <div className="space-y-6">
                    <div className="rounded-lg bg-primary/10 p-6 text-center">
                      <div className="mb-2 font-bold text-3xl text-primary">
                        {results.normalizedFFMI.toFixed(1)}
                      </div>
                      <div className="text-muted-foreground text-sm">
                        Normalize FFMI
                      </div>
                      <div className="mt-1 text-muted-foreground text-xs">
                        (Ham FFMI: {results.ffmi.toFixed(1)})
                      </div>
                    </div>

                    <div className="space-y-3">
                      <h4 className="font-semibold">Kategori:</h4>
                      <div
                        className={`rounded-lg p-3 ${results.category.color}`}
                      >
                        <div className="text-center font-bold">
                          {results.category.level}
                        </div>
                        <div className="mt-1 text-center text-xs">
                          {results.category.desc}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <h4 className="font-semibold">Vücut Kompozisyonu:</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-lg border border-primary/20 bg-primary/10 p-3 text-center">
                          <div className="font-bold text-primary">
                            {results.fatFreeMass.toFixed(1)} kg
                          </div>
                          <div className="text-muted-foreground text-xs">
                            Yağsız Kütle
                          </div>
                        </div>
                        <div className="rounded-lg border border-destructive/20 bg-destructive/10 p-3 text-center">
                          <div className="font-bold text-destructive">
                            {results.fatMass.toFixed(1)} kg
                          </div>
                          <div className="text-muted-foreground text-xs">
                            Yağ Kütlesi
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <h4 className="font-semibold">
                        Araştırma Referansı (kesin sınır değildir):
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center justify-between rounded-lg border border-primary/20 bg-primary/10 p-2">
                          <span>Referans FFMI:</span>
                          <span className="font-bold text-primary">
                            {results.naturalPotential.maxFFMI}
                          </span>
                        </div>
                        <div className="flex items-center justify-between rounded-lg border border-primary/20 bg-primary/10 p-2">
                          <span>Referans yağsız kütle:</span>
                          <span className="font-bold text-primary">
                            {results.naturalPotential.maxLeanMass.toFixed(1)} kg
                          </span>
                        </div>
                        <div className="flex items-center justify-between rounded-lg border border-border bg-accent/30 p-2">
                          <span>%10 yağda referans kilo:</span>
                          <span className="font-bold text-foreground">
                            {results.naturalPotential.maxWeightAt10BF.toFixed(
                              1,
                            )}{" "}
                            kg
                          </span>
                        </div>
                        <div className="flex items-center justify-between rounded-lg border border-muted bg-muted/50 p-2">
                          <span>%15 yağda referans kilo:</span>
                          <span className="font-bold text-foreground">
                            {results.naturalPotential.maxWeightAt15BF.toFixed(
                              1,
                            )}{" "}
                            kg
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="rounded border border-info/20 bg-info/5 p-3 dark:border-info/30 dark:bg-info/10">
                      <p className="text-info-foreground text-sm">
                        <strong>Not:</strong> Bu değerler belirli araştırma
                        örneklemlerinden türetilen karşılaştırma noktalarıdır;
                        kişisel biyolojik sınır veya madde kullanımı kanıtı
                        değildir.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="py-8 text-center text-muted-foreground">
                    Hesaplama yapmak için soldaki formu doldurun
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        <CalculatorArticle content={ffmiGuide} />
      </div>
    </div>
  );
}
